import React, { useEffect, useMemo, useRef, useState } from 'react';

import usePatientToothHistory from './usePatientToothHistory';
import ToothChartContainer from '../../../../charts/plugins/dental/ToothChartContainer';
import useChartType from '../../../../charts/core/useChartType';
import useCoarsePointer from '../../../../hooks/useCoarsePointer';
import useTabletDentalChartStore from '../../../../charts/plugins/dental/tablet/useTabletDentalChartStore';


// Notification-style "Diagnosis" pill — read state persisted in
// localStorage so the highlight clears once the dentist has opened
// the chart at least once after the latest diagnosis was recorded.
//
// Storage shape:
//   key  = `arogya:dx-seen:{patient_id}`
//   value = ISO timestamp of the most-recent diagnosis the dentist
//           has been shown (i.e. the chart was opened after that
//           diagnosis arrived). Newer diagnoses re-highlight the
//           pill automatically.
const DX_SEEN_KEY = (patientId) => `arogya:dx-seen:${patientId}`;

function readSeenAt(patientId) {
  if (!patientId || typeof window === 'undefined') return null;
  try {
    return window.localStorage?.getItem(DX_SEEN_KEY(patientId)) || null;
  } catch (_e) {
    return null;
  }
}

function writeSeenAt(patientId, iso) {
  if (!patientId || typeof window === 'undefined') return;
  try {
    window.localStorage?.setItem(DX_SEEN_KEY(patientId), iso || '');
  } catch (_e) {
    /* ignore */
  }
}

// Inject the pulse keyframe once per app load. Same singleton pattern
// used elsewhere (DailyCollectionSummary, OdontogramSkeleton). The
// pulse drives the "unread diagnosis" red ring on the notification
// pill — once the dentist opens the chart, the pill flips to muted
// styling and the animation stops.
if (typeof document !== 'undefined' && !document.getElementById('arogya-dx-pulse-kf')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'arogya-dx-pulse-kf';
  styleEl.textContent = (
    '@keyframes arogya-dx-pulse {'
    + ' 0%, 100% { box-shadow: 0 0 0 3px rgba(244,63,94,0.35); }'
    + ' 50%      { box-shadow: 0 0 0 6px rgba(244,63,94,0.15); }'
    + '}'
  );
  document.head.appendChild(styleEl);
}

/**
 * Phase 3.1 + 3.1.1 — OdontogramSection.
 *
 * Compact summary row pinned to the disc body. Clicking "View chart"
 * opens the full-size ToothChartDialog where the user can click a
 * tooth to view history / add a note / add a procedure inline (no
 * separate AddProcedureModal pop-up — single canonical surface).
 *
 *   🦷  3 teeth · R(2) F(1) 📝(2)        [View chart →]
 *
 * Props:
 *   visit          — visit row (patient_id + patient_age)
 *   viewer         — { memberId, role } passed through to the panel
 *   palette        — theme tokens from the disc widget
 *   isDark         — boolean
 *   pendingTokens  — string[] of tokens currently being added in the
 *                    half-disc widget's add-procedure flow
 */
// Human-readable chip labels. Dentists scan the slab faster when
// they can read "RCT 2" / "Filled 1" / "Note 4" at a glance instead
// of decoding one-letter codes. Falls back to capitalised kind for
// any new marker types we haven't catalogued yet.
const KIND_LABEL = {
  rct: 'RCT',
  crown: 'Crown',
  filled: 'Filled',
  missing: 'Missing',
  bridge: 'Bridge',
  fractured: 'Fractured',
  impacted: 'Impacted',
  note: 'Note',
  gum: 'Gum',
  diagnosis: 'Diagnosis',
};

const KIND_TINT = {
  rct: { bg: 'bg-rose-100 dark:bg-rose-900/40', border: 'border-rose-400 dark:border-rose-700', text: 'text-rose-900 dark:text-rose-100' },
  crown: { bg: 'bg-violet-100 dark:bg-violet-900/40', border: 'border-violet-400 dark:border-violet-700', text: 'text-violet-900 dark:text-violet-100' },
  filled: { bg: 'bg-teal-100 dark:bg-teal-900/40', border: 'border-teal-400 dark:border-teal-700', text: 'text-teal-900 dark:text-teal-100' },
  missing: { bg: 'bg-slate-200 dark:bg-slate-700', border: 'border-slate-400 dark:border-slate-600', text: 'text-slate-800 dark:text-slate-100' },
  bridge: { bg: 'bg-sky-100 dark:bg-sky-900/40', border: 'border-sky-400 dark:border-sky-700', text: 'text-sky-900 dark:text-sky-100' },
  fractured: { bg: 'bg-amber-100 dark:bg-amber-900/40', border: 'border-amber-400 dark:border-amber-700', text: 'text-amber-900 dark:text-amber-100' },
  impacted: { bg: 'bg-purple-100 dark:bg-purple-900/40', border: 'border-purple-400 dark:border-purple-700', text: 'text-purple-900 dark:text-purple-100' },
  note: { bg: 'bg-amber-50 dark:bg-amber-900/30', border: 'border-amber-300 dark:border-amber-700', text: 'text-amber-900 dark:text-amber-100' },
  gum: { bg: 'bg-rose-50 dark:bg-rose-900/30', border: 'border-rose-300 dark:border-rose-700', text: 'text-rose-900 dark:text-rose-100' },
};

export default function OdontogramSection({
  visit, viewer, palette, isDark, pendingTokens = [],
}) {
  const patientId = visit?.patient_id;
  const visitId = visit?.visit_id;
  // F9 — chart is dentist-only. All hooks run before the early return
  // below to keep React's rules-of-hooks happy.
  const isDentist = useChartType() === 'dental';
  const {
    markers,
    notesByToken,           // diagnosis + note metadata, keyed by token
  } = usePatientToothHistory(isDentist ? patientId : null);

  const [dialogOpen, setDialogOpen] = useState(false);

  // Variant M — on coarse-pointer (tablet/phone) dentists, route every
  // chart-open request through `useTabletDentalChartStore` so the
  // tabletPaired chart is the sole instance on screen. Without this,
  // OdontogramSection auto-opens its own `pane`-mode chart while
  // TabletDentalChartHost also mounts a `tabletPaired` chart for the
  // same visit — two ToothChartContainer instances overlap.
  const isCoarse = useCoarsePointer();
  const openTabletChart = useTabletDentalChartStore((s) => s.open);
  const tabletChartIsOpen = useTabletDentalChartStore((s) => s.isOpen);
  const requestOpen = React.useCallback(() => {
    if (isCoarse && isDentist) {
      openTabletChart(visit, viewer);
    } else {
      setDialogOpen(true);
    }
  }, [isCoarse, isDentist, openTabletChart, visit, viewer]);

  // F8 — widget-mount auto-open. When the half-disc widget mounts
  // OdontogramSection for a visit, the chart opens automatically.
  // Disabled if the dentist flipped the in-chart "Auto-open: OFF"
  // pill (stored as `'false'` in localStorage). Phase 9 leaves this
  // as the sole auto-trigger.
  const openedForVisitRef = useRef(null);
  useEffect(() => {
    if (!visitId) return;
    if (openedForVisitRef.current === visitId) return;
    let pref = null;
    try { pref = window.localStorage?.getItem('arogya:dentist-auto-open-tooth-chart'); }
    catch (_e) { /* private mode */ }
    if (pref === 'false') return;     // explicit opt-out
    requestOpen();
    openedForVisitRef.current = visitId;
  }, [visitId, requestOpen]);

  // F8.5 — explicit open requests (visit-page CTA, future hotkeys).
  useEffect(() => {
    if (!visitId) return undefined;
    const onOpen = (e) => {
      if (e?.detail?.visitId && e.detail.visitId !== visitId) return;
      requestOpen();
      openedForVisitRef.current = visitId;
    };
    window.addEventListener('arogya:open-tooth-chart', onOpen);
    return () => window.removeEventListener('arogya:open-tooth-chart', onOpen);
  }, [visitId, requestOpen]);

  // ── Diagnosis-notification state ────────────────────────────────
  // Compute the latest-diagnosis timestamp across the patient's
  // dental history. The pill highlights when that timestamp is newer
  // than the localStorage-stored "last seen" — i.e. a diagnosis has
  // landed that the dentist hasn't yet opened the chart for.
  const diagnosisStats = useMemo(() => {
    if (!notesByToken) return { count: 0, latestAt: null };
    const seenMeta = new Set();
    let count = 0;
    let latestAt = null;
    for (const entries of notesByToken.values()) {
      if (!Array.isArray(entries)) continue;
      for (const e of entries) {
        // Diagnosis is the entries whose `kind === 'diagnosis'` OR
        // (no kind but a severity is set — heuristic from the hook).
        const isDiagnosis = (
          (e?.kind != null && String(e.kind).toLowerCase() === 'diagnosis')
          || (e?.kind == null && !!e?.severity)
        );
        if (!isDiagnosis) continue;
        const metaId = e?.meta_id;
        if (metaId) {
          if (seenMeta.has(metaId)) continue;
          seenMeta.add(metaId);
        }
        count += 1;
        const ts = e?.created_at;
        if (ts && (!latestAt || ts > latestAt)) latestAt = ts;
      }
    }
    return { count, latestAt };
  }, [notesByToken]);

  // Read the persisted "seen at" once on mount; refresh after we
  // mark seen so the pill demotes to muted without a remount.
  const [seenAt, setSeenAt] = useState(() => readSeenAt(patientId));
  useEffect(() => {
    setSeenAt(readSeenAt(patientId));
  }, [patientId]);
  const hasUnreadDiagnosis = (
    diagnosisStats.count > 0
    && (!seenAt || (diagnosisStats.latestAt && diagnosisStats.latestAt > seenAt))
  );

  // Aggregate kind counts for the summary chips. Phase 3.1.2 — also
  // tally a synthetic 'gum' bucket from the `has_gum` flag so teeth
  // with mixed (procedure + gum) findings show a separate gum chip.
  const kindCounts = useMemo(() => {
    const out = {};
    let gumCount = 0;
    markers.forEach((info) => {
      const k = info?.kind || 'filled';
      out[k] = (out[k] || 0) + (info?.count || 1);
      if (info?.has_gum) gumCount += 1;
    });
    if (gumCount > 0 && !out.gum) out.gum = gumCount;
    return out;
  }, [markers]);

  const distinctTeeth = markers.size;
  const hasHistory = distinctTeeth > 0;
  const hasPending = (pendingTokens || []).length > 0;

  // Always render the entry-point button when there's a visit.
  // Empty-state copy goes inside the dialog — never let the button
  // itself flicker in/out based on a data fetch race.
  if (!visit?.patient_id) return null;
  // F9 — chart is for dentists only. Non-dental staff get no chart
  // entry point and no auto-open. Runs AFTER all hooks above so
  // React's rules-of-hooks stays happy.
  if (!isDentist) return null;

  return (
    <>
      {/* The whole slab is a single click target — previously only the
          'View chart →' link opened the dialog, but dentists kept
          missing the link and tapping the bar. Now the entire row is
          the button; the link stays as a visual affordance. */}
      <button
        type="button"
        onClick={() => {
          // Mark the latest diagnosis as seen the moment the chart
          // opens — that's the notification "read" pulse, so the pill
          // demotes to muted on the next render. Count stays visible.
          if (diagnosisStats.latestAt && diagnosisStats.latestAt !== seenAt) {
            writeSeenAt(patientId, diagnosisStats.latestAt);
            setSeenAt(diagnosisStats.latestAt);
          }
          requestOpen();
        }}
        title="Open tooth chart"
        aria-label="Open tooth chart"
        style={{
          width: '100%',
          textAlign: 'left',
          marginTop: 0,
          padding: '7px 10px',
          borderRadius: 10,
          background: isDark ? 'rgba(15,23,42,0.4)' : '#f8fafc',
          border: `1px solid ${palette?.border || (isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0')}`,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          cursor: 'pointer',
          transition: 'background-color 120ms ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = isDark
            ? 'rgba(15,23,42,0.6)'
            : '#eef2f7';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = isDark
            ? 'rgba(15,23,42,0.4)'
            : '#f8fafc';
        }}
      >
        <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>
          🦷 {hasHistory
            ? `${distinctTeeth} ${distinctTeeth === 1 ? 'tooth' : 'teeth'} with findings`
            : 'Tooth chart'}
        </span>

        {/* Diagnosis notification pill — sits at the head of the chip
            row so it lands in the dentist's first scan. When unread
            (a diagnosis arrived since the last chart open) it pulses
            with a red ring; clicking the slab marks it seen and the
            pill demotes to the same muted style the other count chips
            use. The count stays visible in both states — same pattern
            as a notification bell. */}
        {diagnosisStats.count > 0 && (
          <span
            className={[
              'inline-flex items-center gap-1 px-2 py-0.5 rounded-full border',
              hasUnreadDiagnosis
                ? (isDark
                  ? 'bg-rose-700 border-rose-400 text-white'
                  : 'bg-rose-600 border-rose-400 text-white')
                : (isDark
                  ? 'bg-rose-900/40 border-rose-700 text-rose-100'
                  : 'bg-rose-100 border-rose-400 text-rose-900'),
            ].join(' ')}
            style={{
              fontSize: '0.6875rem',
              fontWeight: 700,
              boxShadow: hasUnreadDiagnosis
                ? '0 0 0 3px rgba(244,63,94,0.35)'
                : 'none',
              animation: hasUnreadDiagnosis
                ? 'arogya-dx-pulse 1.6s ease-in-out infinite'
                : 'none',
            }}
            title={hasUnreadDiagnosis
              ? `${diagnosisStats.count} diagnosis on record — tap to review`
              : `${diagnosisStats.count} diagnosis on record`}
            aria-label={`${diagnosisStats.count} diagnoses recorded${hasUnreadDiagnosis ? ' — unread' : ''}`}
          >
            🩺 Diagnosis
            <span
              style={{
                fontSize: '0.625rem',
                opacity: 0.95,
                padding: '0 5px',
                borderRadius: 8,
                background: 'rgba(255,255,255,0.25)',
              }}
            >
              {diagnosisStats.count}
            </span>
          </span>
        )}

        {/* Kind chips — empty state shows a quiet hint */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', flex: 1 }}>
          {!hasHistory && !hasPending && (
            <span
              style={{
                fontSize: '0.625rem',
                opacity: 0.6,
                fontStyle: 'italic',
              }}
            >
              no dental history yet — tap to add
            </span>
          )}
          {Object.entries(kindCounts).map(([kind, count]) => {
            // 'diagnosis' is rendered separately as the notification
            // pill at the head of the row — skip it in the generic
            // chip loop so it doesn't double-count.
            if (kind === 'diagnosis') return null;
            const tint = KIND_TINT[kind] || KIND_TINT.filled;
            const label = KIND_LABEL[kind] || (kind[0]?.toUpperCase() + kind.slice(1));
            return (
              <span
                key={kind}
                className={[
                  'inline-flex items-center gap-1 px-2 py-0.5 rounded-full border',
                  tint.bg, tint.border, tint.text,
                ].join(' ')}
                style={{ fontSize: '0.6875rem', fontWeight: 700 }}
                title={`${count} × ${label}`}
              >
                <span>{label}</span>
                <span
                  style={{
                    fontSize: '0.625rem',
                    opacity: 0.85,
                    padding: '0 4px',
                    borderRadius: 8,
                    background: isDark ? 'rgba(0,0,0,0.25)' : 'rgba(255,255,255,0.65)',
                  }}
                >
                  {count}
                </span>
              </span>
            );
          })}
          {hasPending && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-dashed border-blue-400 text-blue-700 dark:text-blue-200 dark:border-blue-700"
              style={{ fontSize: '0.6875rem', fontWeight: 700 }}
            >
              + {(pendingTokens || []).join(', ')}
            </span>
          )}
        </div>

        <span
          // Visual affordance only — clicks on the surrounding button
          // also open the dialog. The arrow stays so the cue that "this
          // is interactive" is unambiguous.
          aria-hidden="true"
          className="inline-flex items-center px-2 py-1 rounded-md border border-teal-400 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/40 text-teal-800 dark:text-teal-100"
          style={{ fontSize: '0.6875rem', fontWeight: 600, whiteSpace: 'nowrap' }}
        >
          View chart →
        </span>
      </button>

      {/* Defense in depth — on coarse-pointer / when the tablet chart is
          already mounted by TabletDentalChartHost, never mount our own
          ToothChartContainer (avoids two chart instances overlapping
          during the Variant M tablet flow). */}
      {dialogOpen && !tabletChartIsOpen && !(isCoarse && isDentist) && (
        <ToothChartContainer
          visit={visit}
          viewer={viewer}
          pendingTokens={pendingTokens}
          onClose={() => setDialogOpen(false)}
          isDark={isDark}
        />
      )}
    </>
  );
}
